import React, { useRef, useMemo } from 'react';
import { connect } from 'react-redux';
import { Grid, Typography, Box } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import MainCard from 'ui-component/cards/MainCard';
import LoaderCircular from 'ui-component/LoaderCircular';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { gridSpacing } from 'store/constant';
import { getDynamicColumns } from '../../utils/dynamicColumns';
import '../Dashboard/styleSearch.scss';
import ScrollIndicator from '../scroller/ScrollIndicator';

function splitDigits(num) {
  if (num == null || num === '' || num === undefined || isNaN(num)) return '-';
  const numValue = typeof num === 'string' ? parseFloat(num) : num;
  if (isNaN(numValue)) return '-';
  return Number(numValue).toLocaleString();
}
function roundToTwoDecimal(value) {
  if (value == null || value == '-999') return '-';
  return (Math.round((value + Number.EPSILON) * 10000) / 10000).toFixed(4);
}
function roundYen(value) {
  if (value == null || value == '-999') return '-';
  return Math.round(value);
}

const columns = [
  {
    field: 'campaign_name',
    headerName: 'Campaign Name',
    flex: 2,
    minWidth: 200,
    align: 'left',
    headerAlign: 'left',
    renderCell: (params) => <div className="campaign_name_settings">{params.value}</div>
  },
  {
    field: 'country',
    headerName: 'Country',
    flex: 0.8,
    minWidth: 100,
    align: 'left',
    headerAlign: 'left'
  },
  {
    field: 'state',
    headerName: 'State',
    flex: 1,
    minWidth: 120,
    align: 'left',
    headerAlign: 'left',
    renderCell: (params) => (
      <div className="country_name_settings" style={{ textAlign: 'left' }}>
        {params.value}
      </div>
    )
  },
  {
    field: 'os_name',
    headerName: 'OS',
    flex: 0.6,
    minWidth: 80,
    align: 'left',
    headerAlign: 'left'
  },
  {
    field: 'clicks',
    headerName: 'Clicks',
    valueFormatter: (params) => splitDigits(params.value),
    flex: 1,
    minWidth: 100,
    align: 'left',
    headerAlign: 'left'
  },
  {
    field: 'impressions',
    headerName: 'Impressions',
    valueFormatter: (params) => splitDigits(params.value),
    flex: 1,
    minWidth: 120,
    align: 'left',
    headerAlign: 'left'
  },
  {
    field: 'media_cost',
    headerName: 'Media Cost ($)',
    valueFormatter: (params) => splitDigits(roundToTwoDecimal(params.value)),
    flex: 1,
    minWidth: 140,
    align: 'left',
    headerAlign: 'left'
  },
  {
    field: 'installs',
    headerName: 'MMP Installs',
    flex: 1,
    minWidth: 120,
    align: 'left',
    headerAlign: 'left',
    valueFormatter: (params) => splitDigits(params.value)
  },
  {
    field: 'kpi_event_count',
    headerName: 'KPI Events',
    flex: 1,
    minWidth: 120,
    align: 'left',
    headerAlign: 'left',
    valueFormatter: (params) => splitDigits(params.value)
  },
  {
    field: 'ctr',
    headerName: 'CTR',
    valueFormatter: (params) => {
      if (params.value == null || params.value == '-999') return '-';
      const roundedValue = Math.round((params.value * 100 + Number.EPSILON) * 10000) / 10000;
      return `${roundedValue.toFixed(4)}%`;
    },
    flex: 0.8,
    minWidth: 80,
    align: 'left',
    headerAlign: 'left'
  },
  {
    field: 'ecpm',
    headerName: 'eCPM ($)',
    valueFormatter: (params) => splitDigits(roundToTwoDecimal(params.value)),
    flex: 0.8,
    minWidth: 80,
    align: 'left',
    headerAlign: 'left'
  },
  {
    field: 'ecpc',
    headerName: 'eCPC ($)',
    valueFormatter: (params) => splitDigits(roundToTwoDecimal(params.value)),
    flex: 0.8,
    minWidth: 80,
    align: 'left',
    headerAlign: 'left'
  },
  {
    field: 'ecpi',
    headerName: 'eCPI ($)',
    valueFormatter: (params) => splitDigits(roundToTwoDecimal(params.value)),
    flex: 0.8,
    minWidth: 80,
    align: 'left',
    headerAlign: 'left'
  }
];

const StateReportSkanView = ({ stateReportData, loading, error }) => {
  const gridContainerRef = useRef(null);

  // Get dynamic columns based on available data
  const dynamicColumns = useMemo(() => {
    return getDynamicColumns(columns, stateReportData || []);
  }, [stateReportData]);

  return (
    <>
      {loading ? (
        <MainCard>
          <Grid container spacing={gridSpacing}>
            <Grid item lg={12} md={12} sm={12} xs={12} alignContent="center" sx={{ textAlign: 'center' }}>
              <Typography variant="h4">State Level Report</Typography>
            </Grid>
            <Grid item xs={12} alignContent="center" sx={{ textAlign: 'center' }}>
              <div style={{ height: '200px', width: '100%' }}>
                <LoaderCircular show={true} />
              </div>
            </Grid>
          </Grid>
        </MainCard>
      ) : !stateReportData?.length ? (
        <MainCard>
          <Grid container spacing={gridSpacing}>
            <Grid item lg={12} md={12} sm={12} xs={12} alignContent="center" sx={{ textAlign: 'center' }}>
              <Typography variant="h4">State Level Report</Typography>
            </Grid>
            <Grid item xs={12} alignContent="center" sx={{ textAlign: 'center' }}>
              <Typography style={{ textAlign: 'center' }}>
                {error ? (
                  'No Data Found'
                ) : (
                  <span>
                    Please click <b>Apply Filter</b> to view data
                  </span>
                )}
              </Typography>
            </Grid>
          </Grid>
        </MainCard>
      ) : (
        <MainCard>
          <Grid container spacing={gridSpacing}>
            <Grid item lg={12} md={12} sm={12} xs={12} alignContent="center" sx={{ textAlign: 'center' }}>
              <Typography variant="h4">State Level Report</Typography>
            </Grid>
            <Grid item xs={12}>
              <Grid container alignItems="center" justifyContent="space-between">
                <Grid item xs={12}>
                  <Box sx={{ position: 'relative' }}>
                    <div
                      ref={gridContainerRef}
                      style={{
                        width: '100%',
                        position: 'relative',
                        overflowX: 'auto',
                        overflowY: 'hidden'
                      }}
                    >
                      <style>
                        {`
                          .custom-datagrid {
                            width: 100% !important;
                          }
                          .custom-datagrid .MuiDataGrid-main {
                            width: 100% !important;
                          }
                          .custom-datagrid .MuiDataGrid-virtualScroller {
                            width: 100% !important;
                          }
                        `}
                      </style>
                      <ScrollIndicator containerRef={gridContainerRef} />
                      {loading ? (
                        <CircularProgress
                          style={{
                            position: 'relative',
                            top: '50%',
                            left: '50%'
                          }}
                        />
                      ) : (
                        <DataGrid
                          rows={stateReportData}
                          columns={dynamicColumns}
                          initialState={{
                            pagination: {
                              paginationModel: {
                                pageSize: 10
                              }
                            },
                            sorting: {
                              sortModel: [{ field: 'media_cost', sort: 'desc' }]
                            }
                          }}
                          components={{
                            LoadingOverlay: CircularProgress
                          }}
                          loading={loading}
                          pageSizeOptions={[10, 20, 30]}
                          slots={{ toolbar: GridToolbar }}
                          disableRowSelectionOnClick
                          disableColumnSelector
                          disableDensitySelector
                          autoHeight
                          getRowHeight={() => 'auto'}
                          className="custom-datagrid"
                        />
                      )}
                    </div>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </MainCard>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  stateReportData: state.stateReportReducer.stateReportData,
  loading: state.stateReportReducer.loading,
  error: state.stateReportReducer.error
});

export default connect(mapStateToProps, null)(StateReportSkanView);
